import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import ReadingProgress from "../components/articles/readingProgress";
import CTABlock from "../components/articles/ctaBlock";
import RelatedPosts from "../components/articles/relatedPosts";
import VideoEmbed from "../components/articles/videoEmbed";
import ShareBar from "../components/common/shareBar";
import { getArtifact } from "../components/articles/artifacts";
import Notfound from "./404";

import INFO from "../data/user";
import { getArticleBySlug } from "../data/articles";

import "./styles/readArticle.css";

const ReadArticle = () => {
	const navigate = useNavigate();
	const { slug } = useParams();

	const article = getArticleBySlug(slug);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [slug]);

	if (!article) {
		return <Notfound />;
	}

	const Artifact = getArtifact(article.artifact);

	// Absolute URL and share image for this article, used by the OpenGraph and
	// Twitter tags so link unfurls (LinkedIn, X, Slack, iMessage) show the
	// article's own headline and image. The post-build prerender script bakes
	// these same values into static HTML so crawlers, which do not run JS, see
	// them too.
	const SITE = "https://swapniltamse.com";
	const pageUrl = `${SITE}/article/${slug}`;
	const heroSrc = article.hero && article.hero.src ? article.hero.src : "";
	const ogImage = heroSrc
		? heroSrc.startsWith("http")
			? heroSrc
			: `${SITE}${heroSrc.startsWith("/") ? "" : "/"}${heroSrc}`
		: `${SITE}/homepage.jpg`;

	// Render markdown images as figures so inline images can carry a caption
	// (the caption comes from the image alt text).
	const markdownComponents = {
		img: ({ node, ...props }) => (
			<figure className="article-figure">
				<img {...props} alt={props.alt || ""} />
				{props.alt ? <figcaption>{props.alt}</figcaption> : null}
			</figure>
		),
	};

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${article.title} | ${INFO.main.title}`}</title>
				<meta name="description" content={article.description} />
				<meta
					name="keywords"
					content={article.keywords.join(", ")}
				/>
				<link rel="canonical" href={pageUrl} />
				<meta property="og:type" content="article" />
				<meta property="og:url" content={pageUrl} />
				<meta property="og:title" content={article.title} />
				<meta
					property="og:description"
					content={article.description}
				/>
				<meta property="og:image" content={ogImage} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={article.title} />
				<meta
					name="twitter:description"
					content={article.description}
				/>
				<meta name="twitter:image" content={ogImage} />
			</Helmet>

			<ReadingProgress />

			<div className="page-content">
				<NavBar />

				<div className="content-wrapper">
					<div className="read-article-logo-container">
						<div className="read-article-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="read-article-container">
						<div className="read-article-back">
							<img
								src="../back-button.png"
								alt="back"
								className="read-article-back-button"
								onClick={() => navigate(-1)}
							/>
						</div>

						<div className="read-article-wrapper">
							<div className="read-article-meta">
								<span className="read-article-date">
									{article.date}
								</span>
								<span className="read-article-dot">•</span>
								<span className="read-article-readtime">
									{article.readingTime} min read
								</span>
								{article.updated && (
									<React.Fragment>
										<span className="read-article-dot">
											•
										</span>
										<span className="read-article-updated">
											Updated {article.updated}
										</span>
									</React.Fragment>
								)}
							</div>

							<div className="title read-article-title">
								{article.title}
							</div>

							{article.hero && (
								<figure className="read-article-hero">
									<img
										src={article.hero.src}
										alt={article.hero.alt || ""}
									/>
									{article.hero.caption ? (
										<figcaption>
											{article.hero.caption}
										</figcaption>
									) : null}
								</figure>
							)}

							{article.tldr && (
								<div className="read-article-tldr">
									<span className="read-article-tldr-label">
										TL;DR
									</span>
									{article.tldr}
								</div>
							)}

							<ShareBar
								title={article.title}
								kind="article"
								prompt=""
							/>

							<div className="read-article-body">
								<ReactMarkdown
									remarkPlugins={[remarkGfm]}
									components={markdownComponents}
								>
									{article.markdown}
								</ReactMarkdown>
							</div>

							{article.video && (
								<VideoEmbed url={article.video} />
							)}

							{Artifact && (
								<div className="read-article-artifact">
									<Artifact />
								</div>
							)}

							{article.provenance && (
								<div className="read-article-provenance">
									<a
										href={
											article.provenanceUrl ||
											INFO.socials.linkedin
										}
										target="_blank"
										rel="noopener noreferrer"
									>
										{article.provenance}
									</a>
								</div>
							)}

							<div className="read-article-discuss">
								<a
									href={
										article.provenanceUrl ||
										INFO.socials.linkedin
									}
									target="_blank"
									rel="noopener noreferrer"
								>
									Continue the conversation on LinkedIn →
								</a>
							</div>

							<ShareBar
								title={article.title}
								kind="article"
							/>

							<RelatedPosts slug={slug} />

							<CTABlock />
						</div>
					</div>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default ReadArticle;
